/* eslint-disable react/display-name */

import React, { useEffect, useMemo, useState } from 'react';
import useSWR from "swr";
import { config } from "../core";
import 'chartjs-plugin-colorschemes';
import { Doughnut, HorizontalBar, Bar, Line, Pie } from "react-chartjs-2";
import DashboardCard, { InfoIcon } from "../components/cards/DashboardCard";
import DropdownButton from "../components/Buttons/DropdownButton";
import ArrowDropDown from "@material-ui/icons/ArrowDropDown";
import IncomingInspectionsMap from "../components/IncomingInspectionsMap";
import IncomingMonthlyTrend from "../components/IncomingMonthlyTrend";
import PipelineTab from "../components/PipelineTab";
import InspectorUtilisationTab from "../components/InspectorUtilisationTab";
import SchedulerUtilisationTab from "../components/SchedulerUtilisationTab";
import HealthMapTab from "../components/HealthMapTab";
import OutOfAreaTab from "../components/OutOfAreaTab";
import NaturalLanguageReportTab from "../components/NaturalLanguageReportTab";
import CompletedTab from "../components/CompletedTab";
import { getRoles } from "../api/core/provider";
import { InspectorTable as LateDashInspectorTable, StateTable as LateDashStateTable } from "../components/late/LateDashTables";
import TabPanel from "../components/Tabs/TabPanel";
import { CircularProgress, Tabs, Tab } from "@material-ui/core";
import { Dialog, DialogContent } from "@material-ui/core";
import InspectionPreContact from "./InspectionPreContact";
import { withStyles } from "@material-ui/core/styles";
import { useSortBy, useTable } from "react-table";
import { apiReports } from "../api/reports";
import { apiInspections } from "../api/inspections";
import { useTour } from "../tours/useTour";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DateTime } from "luxon";

// --- Shared constants ---
const categoryNames = ['Scheduled', 'Part Scheduled', 'Not Scheduled', 'New'];
const chartColors = ['#94ba33', '#F8ED00', '#d71433', '#00acdc'];
const cardColors = ['tw-bg-fs-green', 'tw-bg-yellow-400', 'tw-bg-fs-red', 'tw-bg-primary'];
const lateNames = ['Scheduled', 'Part Scheduled', 'Not Scheduled'];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Late Report colors
const red = '#d71433';
const yellow = '#F8ED00';
const amber = 'rgba(251,191,36)';
const green = '#94ba33';
const black = '#000000';

// --- Styled tabs ---
const StyledTabs = withStyles((theme) => ({
  indicator: {
    backgroundColor: theme.palette.primary.main,
  },
}))((props) => <Tabs {...props} />);

const StyledTab = withStyles((theme) => ({
  root: {
    color: '#000000',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(15),
    textTransform: "uppercase",
    letterSpacing: "-.01em",
    '&:focus': { opacity: 1 },
  },
}))((props) => <Tab disableRipple {...props} />);

// --- Chart options ---
const doughnutOptions = {
  legend: { display: false },
  maintainAspectRatio: false,
  cutoutPercentage: 55,
};

const lateBarOptions = {
  legend: { display: true, position: 'bottom' },
  maintainAspectRatio: false,
  scales: {
    xAxes: [{ stacked: true, ticks: { beginAtZero: true, precision: 0 } }],
    yAxes: [{ stacked: true }],
  },
  tooltips: { mode: 'index', intersect: false },
};

const livePieOptions = { legend: { display: false, position: 'left' } };
const MonthlyPieOptions = { legend: { display: true, position: 'left' } };

// --- Helpers ---
// sentiment: "bad" = up is red, "neutral" = always gray
const ChangeIndicator = ({ change, sentiment = "bad" }) => {
  if (change === 0) {return <span className="tw-text-gray-400 tw-text-xs">no change</span>;}
  const isUp = change > 0;
  let colorClass = "tw-text-gray-500";
  if (sentiment === "bad") {colorClass = isUp ? "tw-text-red-600" : "tw-text-green-600";}
  return (
    <span className={`tw-font-semibold ${colorClass}`}>
      <i className={`fa fa-arrow-${isUp ? 'up' : 'down'} tw-mr-1`} />
      {Math.abs(change)} {isUp ? "more" : "fewer"} than yesterday
    </span>
  );
};

const line = <p className={"tw-border-secondary tw-bg-secondary tw-py-1 tw-border-0.5 tw-rounded-lg tw-mb-4"}/>;

const GetComingDue = ({ ComingDue }) => ComingDue;
const GetLate = ({ Open, Late }) => {
  if (Open === 0) {return '0 (0%)';}
  return `${Late} (${(Late / Open * 100).toFixed(0)}%)`;
};

// --- Inspector Table (from Late Report) ---
const inspectorPieOptions = { maintainAspectRatio: false, legend: { display: false }, tooltips: { enabled: false } };

const InspectorTable = ({ data }) => {
  const columns = useMemo(() => [
    { Header: "Inspector", accessor: "Inspector" },
    { Header: "Assigned", accessor: "Assigned" },
    { Header: "Submitted", accessor: "Submitted" },
    { Header: "Turnover", accessor: "Turnover", Cell: props => `${props.row.original.Turnover.toFixed(1)}%` },
    { Header: "Late", accessor: "Late", Cell: props => `${props.row.original.Late} (${props.row.original.LatePercent.toFixed(1)}%)` },
    { Header: "", accessor: "chart", Cell: props => {
      const onTime = (Number(props.row.original.Submitted) || 0) - (Number(props.row.original.Late) || 0);
      const late = Number(props.row.original.Late) || 0;
      return <div className={"tw-w-20 tw-h-14"}><Pie data={{ datasets: [{ data: [onTime, late], backgroundColor: [green, red] }], labels: ["On Time", "Late"] }} options={inspectorPieOptions} /></div>;
    } },
  ], []);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns, data, initialState: { sortBy: [{ id: "Inspector", desc: false }] },
  }, useSortBy);

  return (
    <table className="tw-mt-10 tw-min-w-full tw-divide-y tw-divide-gray-200" {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup, i) =>
          <tr key={`${i}`} className={"tw-bg-fs-dark-blue tw-bg-opacity-25 tw-rounded-t-lg"} {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column, j) =>
              <th key={`${i}-${j}`} className={`tw-px-6 tw-py-3 tw-text-left tw-leading-4 tw-font-medium tw-uppercase tw-tracking-wider tw-font-bold ${column.isSorted ? column.isSortedDesc ? 'sort-desc' : 'sort-asc' : ''}`}
                {...column.getHeaderProps(column.getSortByToggleProps())}>
                {column.render("Header")} {column.isSorted ? column.isSortedDesc ? <div className="tw-absolute tw-inline tw--mt-1 tw-ml-1"><i className="fa fa-sort-down"/></div> : <div className="tw-absolute tw-inline tw--mt-1 tw-ml-1"><i className="fa fa-sort-up"/></div> : ''}
              </th>
            )}
          </tr>
        )}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <tr key={`${i}`} {...row.getRowProps()}>
              {row.cells.map((cell, j) => {
                let classnames = "tw-px-6 tw-py-2 ";
                i % 2 ? classnames = classnames + "tw-bg-gray-200" : null;
                return <td key={`${i}-${j}`} className={classnames} {...cell.getCellProps()}>{cell.render("Cell")}</td>;
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// =============================================
// MAIN COMPONENT
// =============================================
const SupportDashboard = () => {
  const isSuperAdmin = getRoles().includes("superadmin");
  const TABS = [
    { label: "Scheduling", anchor: "scheduling" },
    { label: "Completed", anchor: "completed" },
    { label: "Late Management", anchor: "late" },
    { label: "Penalty Risk", anchor: "penalty-risk" },
    ...config.naturalLanguageReportsEntitled ? [{ label: "Ask Reports", anchor: "ask-reports" }] : [],
    ...config.inspectorUtilisationEnabled ? [{ label: "Inspector Utilisation", anchor: "utilisation" }] : [],
    ...config.schedulerUtilisationEnabled ? [{ label: "Scheduler Utilisation", anchor: "scheduler-utilisation" }] : [],
    ...config.healthMapEnabled ? [{ label: "Health Map", anchor: "health-map" }] : [],
    ...config.outOfAreaEntitled ? [{ label: "Out of Area", anchor: "out-of-area" }] : [],
    ...isSuperAdmin ? [{ label: "Penalty Management", anchor: "penalty-management" }] : [],
  ];
  const tabIdx = (anchor) => TABS.findIndex((t) => t.anchor === anchor);
  const [activeTab, setActiveTab] = useState(() => {
    const h = (window.location.hash || "").replace(/^#/, "");
    const idx = TABS.findIndex((t) => t.anchor === h);
    return idx >= 0 ? idx : 0;
  });
  const [riskFilter, setRiskFilter] = useState(null);
  const [contactPolicyNo, setContactPolicyNo] = useState(null);
  // Penalty Risk client-side search (policy no / address / city / state).
  const [riskSearch, setRiskSearch] = useState('');
  // Photo popup: policy number of the inspection whose photos are shown.
  const [photoPopupPolicyNo, setPhotoPopupPolicyNo] = useState(null);

  // Guided tour for the Penalty Risk tab (fires once per user, first visit).
  const tourSteps = useMemo(() => [
    {
      element: '[data-tour=pr-cards]',
      popover: {
        title: "Risk filter cards",
        description: "Each card counts a category of at-risk inspections and filters the table when clicked — the selected card lifts forward. The subtitle shows how many of your claimed items fall in that category.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour=pr-toolbar]',
      popover: {
        title: "Totals and search",
        description: "Shows how many rows match out of the total. Search by policy number, address, city, or state, and clear the card filter here.",
        side: "bottom", align: "end",
      },
    },
    {
      element: '[data-tour=pr-mylist]',
      popover: {
        title: "My List",
        description: "Inspections you've claimed, split into Action required and No action required. Select rows in the table and use Claim selected (or Claim selected - no action); release or switch list from each row's Actions menu.",
        side: "bottom", align: "start",
      },
    },
    {
      element: '[data-tour=pr-table]',
      popover: {
        title: "Risk table",
        description: "Every column sorts. Vis. shows a green tick when enough photos were recorded (the inspector did the work) — click the tick to view them. Ext. tracks Orchid extensions: double tick approved (orange closing, red deadline reached), single tick awaiting Orchid (gray, orange 3+ days, red 7+ days).",
        side: "top", align: "start",
      },
    },
  ], []);
  const { startTour, showButton } = useTour({ tourKey: "penalty-risk", steps: tourSteps, enabled: activeTab === 3 });
  // Penalty Risk table column sort. Null sortKey keeps the backend urgency order.
  const [riskSortKey, setRiskSortKey] = useState(null);
  const [riskSortDir, setRiskSortDir] = useState('asc');
  // Penalty Risk working-list: inspections selected in the main table to claim.
  const [riskSelected, setRiskSelected] = useState(new Set());
  // When set, my working lists show only items matching the active card filter.
  const [listsMatchFilter, setListsMatchFilter] = useState(false);

  // --- Scheduling tab data ---
  const { data: dashData, error } = useSWR(
    `${config.api.baseUrl}/reports/supportdashboard`,
    { refreshInterval: 30000 }
  );

  // --- Late Management tab data ---
  const { data: lateDashData } = useSWR(
    `${config.api.baseUrl}/reports/inspectionlates/dash`,
    { refreshInterval: 60000 }
  );

  // --- Sparkline metric history ---
  const metricKeys = [
    "sched_total_open", "sched_scheduled", "sched_part_scheduled", "sched_not_scheduled", "sched_new",
    "sched_scheduled_late", "sched_part_scheduled_late", "sched_not_scheduled_late",
    "live_all_open", "live_late_30", "live_coming_due_5", "live_submitted_30", "live_received_today", "live_received_30",
  ];

  const historyByMetric = {};
  metricKeys.forEach((key) => {
    const { data } = useSWR(`metric-history-${key}`, () => apiReports.getMetricHistory(key, 30), { refreshInterval: 0 });
    historyByMetric[key] = data?.data?.result || null;
  });

  const loading = !dashData && !error;
  const categories = dashData?.result?.Categories || [];
  const total = dashData?.result?.Total || 0;
  const totalChange = dashData?.result?.TotalChange;
  const hasYesterday = categories.some(c => c.YesterdayCount > 0);
  const lateCategories = categories.slice(0, 3);

  const schedByMonth = dashData?.result?.ScheduledByMonth;
  const scheduledMonthLine = schedByMonth
    ? `${schedByMonth.CurrentLabel || '?'}: ${schedByMonth.Current ?? 0} · ${schedByMonth.NextLabel || '?'}: ${schedByMonth.Next ?? 0} · ${schedByMonth.FollowingLabel || '?'}: ${schedByMonth.Following ?? 0}`
    : null;

  const doughnutData = categories.length > 0 ? {
    labels: categories.map(c => c.Name),
    datasets: [{ data: categories.map(c => c.Count), backgroundColor: chartColors, borderWidth: 2, borderColor: '#ffffff' }],
  } : null;

  // Turnaround data processing
  const allTurnaround = dashData?.result?.Turnaround || [];
  const now = new Date();
  const cutoff = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const monthlyTurnaround = allTurnaround.filter(t => new Date(t.Year, t.Month - 1, 1) >= cutoff);

  const yearlyMap = {};
  allTurnaround.forEach(t => {
    if (!yearlyMap[t.Year]) {yearlyMap[t.Year] = { volume: 0, avgSum: 0, medSum: 0, d0: 0, d8: 0, d15: 0, d22: 0, d31: 0, d45: 0 };}
    const y = yearlyMap[t.Year];
    y.volume += t.Volume; y.avgSum += t.Average * t.Volume; y.medSum += t.Median * t.Volume;
    y.d0 += t.Days0to7; y.d8 += t.Days8to14; y.d15 += t.Days15to21;
    y.d22 += t.Days22to30; y.d31 += t.Days31to45; y.d45 += t.Days45Plus;
  });
  const years = Object.keys(yearlyMap).sort();

  const distColors = {
    d0: { bg: 'rgba(34, 197, 94, 0.8)', border: '#22c55e' }, d8: { bg: 'rgba(132, 204, 22, 0.8)', border: '#84cc16' },
    d15: { bg: 'rgba(250, 204, 21, 0.8)', border: '#facc15' }, d22: { bg: 'rgba(251, 146, 60, 0.8)', border: '#fb923c' },
    d31: { bg: 'rgba(239, 68, 68, 0.8)', border: '#ef4444' }, d45: { bg: 'rgba(127, 29, 29, 0.8)', border: '#7f1d1d' },
  };

  const buildTrendData = (items, labels) => ({
    labels,
    datasets: [
      { type: 'bar', label: 'Inspection Volume', data: items.map(t => t.volume || t.Volume), backgroundColor: 'rgba(0, 172, 220, 0.7)', borderColor: '#00acdc', borderWidth: 1, yAxisID: 'y-volume', order: 2 },
      { type: 'line', label: 'Average Days', data: items.map(t => { const avg = t.avgSum ? t.avgSum / (t.volume || 1) : t.Average; return Math.round(avg * 10) / 10; }), borderColor: '#45456e', backgroundColor: '#45456e', borderWidth: 2, pointRadius: 3, fill: false, yAxisID: 'y-days', order: 1 },
      { type: 'line', label: 'Median Days', data: items.map(t => { const med = t.medSum ? t.medSum / (t.volume || 1) : t.Median; return Math.round(med * 10) / 10; }), borderColor: '#5fb600', backgroundColor: '#5fb600', borderWidth: 2, pointRadius: 3, borderDash: [5, 5], fill: false, yAxisID: 'y-days', order: 0 },
    ],
  });

  const trendOptions = {
    maintainAspectRatio: false,
    tooltips: { mode: 'index', intersect: false, callbacks: { label: function(item, data) { const label = data.datasets[item.datasetIndex].label; const val = item.yLabel; if (label.includes('Days')) {return `${label}: ${val.toFixed(1)} days`;} return `${label}: ${val.toLocaleString()}`; } } },
    scales: {
      yAxes: [
        { id: 'y-volume', type: 'linear', position: 'left', ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: 'Inspection Volume' } },
        { id: 'y-days', type: 'linear', position: 'right', ticks: { beginAtZero: true }, gridLines: { drawOnChartArea: false }, scaleLabel: { display: true, labelString: 'Turnaround Days' } },
      ],
      xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 14 } }],
    },
    legend: { position: 'top' },
  };

  const buildDistData = (items, labels) => {
    const mapped = items.map(t => ({ d0: t.d0 ?? t.Days0to7 ?? 0, d8: t.d8 ?? t.Days8to14 ?? 0, d15: t.d15 ?? t.Days15to21 ?? 0, d22: t.d22 ?? t.Days22to30 ?? 0, d31: t.d31 ?? t.Days31to45 ?? 0, d45: t.d45 ?? t.Days45Plus ?? 0 }));
    const pct = (key) => mapped.map(t => { const tot = t.d0 + t.d8 + t.d15 + t.d22 + t.d31 + t.d45; return tot > 0 ? t[key] / tot * 100 : 0; });
    return {
      labels,
      datasets: [
        { label: '0-7 days', data: pct('d0'), backgroundColor: distColors.d0.bg, borderColor: distColors.d0.border, borderWidth: 1 },
        { label: '8-14 days', data: pct('d8'), backgroundColor: distColors.d8.bg, borderColor: distColors.d8.border, borderWidth: 1 },
        { label: '15-21 days', data: pct('d15'), backgroundColor: distColors.d15.bg, borderColor: distColors.d15.border, borderWidth: 1 },
        { label: '22-30 days', data: pct('d22'), backgroundColor: distColors.d22.bg, borderColor: distColors.d22.border, borderWidth: 1 },
        { label: '31-45 days', data: pct('d31'), backgroundColor: distColors.d31.bg, borderColor: distColors.d31.border, borderWidth: 1 },
        { label: '45+ days', data: pct('d45'), backgroundColor: distColors.d45.bg, borderColor: distColors.d45.border, borderWidth: 1 },
      ],
    };
  };

  const distOptions = {
    maintainAspectRatio: false, legend: { position: 'top' },
    tooltips: { mode: 'index', intersect: false, callbacks: { label: function(item, data) { return `${data.datasets[item.datasetIndex].label}: ${item.yLabel.toFixed(1)}%`; } } },
    scales: { xAxes: [{ stacked: true }], yAxes: [{ stacked: true, ticks: { max: 100, callback: v => v + '%' }, scaleLabel: { display: true, labelString: 'Percentage' } }] },
  };

  const monthlyTrendData = monthlyTurnaround.length > 0 ? buildTrendData(monthlyTurnaround, monthlyTurnaround.map(t => `${MONTH_NAMES[t.Month - 1]} ${t.Year}`)) : null;
  const monthlyDistData = monthlyTurnaround.length > 0 ? buildDistData(monthlyTurnaround, monthlyTurnaround.map(t => `${MONTH_NAMES[t.Month - 1]} ${t.Year}`)) : null;
  const yearlyTrendData = years.length > 0 ? buildTrendData(years.map(y => yearlyMap[y]), years) : null;
  const yearlyDistData = years.length > 0 ? buildDistData(years.map(y => yearlyMap[y]), years) : null;

  // Square footage data processing
  const allSqft = dashData?.result?.SqftYears || [];

  const sqftTrendData = allSqft.length > 0 ? {
    labels: allSqft.map(s => s.Year.toString()),
    datasets: [{
      label: 'Average Sq Ft',
      data: allSqft.map(s => s.Average),
      borderColor: 'rgba(0, 172, 220, 1)',
      backgroundColor: 'rgba(0, 172, 220, 0.1)',
      borderWidth: 2,
      fill: true,
      lineTension: 0.3,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#fff',
      pointBorderColor: 'rgba(0, 172, 220, 1)',
      pointBorderWidth: 2,
    }],
  } : null;

  const sqftTrendOptions = {
    maintainAspectRatio: false,
    title: { display: true, text: 'Average Square Footage by Year', fontSize: 16 },
    legend: { display: false },
    tooltips: { callbacks: { label: function(item) { const idx = item.index; const count = allSqft[idx]?.Count || 0; return [`Average: ${item.yLabel.toLocaleString()} sq ft`, `Inspections: ${count.toLocaleString()}`]; } } },
    scales: {
      yAxes: [{ ticks: { callback: v => v.toLocaleString() }, scaleLabel: { display: true, labelString: 'Average Square Footage' } }],
      xAxes: [{ scaleLabel: { display: true, labelString: 'Year' } }],
    },
  };

  const sqftBands = [
    { key: 'Under1000', label: '< 1,000' },
    { key: 'Range1kTo2k', label: '1,000 - 2,000' },
    { key: 'Range2kTo3k', label: '2,000 - 3,000' },
    { key: 'Range3kTo5k', label: '3,000 - 5,000' },
    { key: 'Range5kTo10k', label: '5,000 - 10,000' },
    { key: 'Range10kTo25k', label: '10,000 - 25,000' },
    { key: 'Over25000', label: '25,000+' },
  ];
  const sqftDistTotals = sqftBands.map(b => allSqft.reduce((sum, s) => sum + (s[b.key] || 0), 0));
  const sqftDistTotal = sqftDistTotals.reduce((a, b) => a + b, 0);

  const sqftDistColors = [
    'rgba(147, 197, 253, 0.8)', 'rgba(96, 165, 250, 0.8)', 'rgba(59, 130, 246, 0.8)',
    'rgba(37, 99, 235, 0.8)', 'rgba(29, 78, 216, 0.8)', 'rgba(30, 64, 175, 0.8)', 'rgba(88, 28, 135, 0.8)',
  ];

  const sqftDistData = sqftDistTotal > 0 ? {
    labels: sqftBands.map(b => b.label),
    datasets: [{
      label: 'Inspections',
      data: sqftDistTotals,
      backgroundColor: sqftDistColors,
      borderColor: sqftDistColors.map(c => c.replace('0.8', '1')),
      borderWidth: 1,
    }],
  } : null;

  const sqftDistOptions = {
    maintainAspectRatio: false,
    legend: { display: false },
    tooltips: { callbacks: { label: function(item) { const count = item.xLabel; const pct = sqftDistTotal > 0 ? (count / sqftDistTotal * 100).toFixed(1) : 0; return `${count.toLocaleString()} inspections (${pct}%)`; } } },
    scales: {
      xAxes: [{ ticks: { beginAtZero: true, callback: v => v.toLocaleString() }, scaleLabel: { display: true, labelString: 'Number of Inspections' } }],
      yAxes: [{ scaleLabel: { display: true, labelString: 'Square Footage' } }],
    },
  };

  // Quality of Property data processing
  const QUALITY_SCORE_KEYS = ['Score1', 'Score2', 'Score3', 'Score4', 'Score5', 'Score6', 'Score7', 'Score8', 'Score9', 'Score10'];
  const QUALITY_SCORE_COLORS = [
    { bg: 'rgba(220, 38, 38, 0.8)', border: '#dc2626' }, // 1 - red
    { bg: 'rgba(239, 68, 68, 0.8)', border: '#ef4444' }, // 2 - red
    { bg: 'rgba(249, 115, 22, 0.8)', border: '#f97316' }, // 3 - orange
    { bg: 'rgba(251, 146, 60, 0.8)', border: '#fb923c' }, // 4 - orange
    { bg: 'rgba(250, 204, 21, 0.8)', border: '#facc15' }, // 5 - yellow
    { bg: 'rgba(234, 179, 8, 0.8)', border: '#eab308' }, // 6 - yellow
    { bg: 'rgba(163, 230, 53, 0.8)', border: '#a3e635' }, // 7 - lime
    { bg: 'rgba(132, 204, 22, 0.8)', border: '#84cc16' }, // 8 - lime
    { bg: 'rgba(34, 197, 94, 0.8)', border: '#22c55e' }, // 9 - green
    { bg: 'rgba(22, 163, 74, 0.8)', border: '#16a34a' }, // 10 - green
  ];

  const allQuality = dashData?.result?.QualityMonths || [];
  const qualityFrom2018 = allQuality.filter(q => q.Year >= 2018);

  // Monthly: last 12 months, group by year-month
  const qualityCutoff = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  const qualityMonthlyFiltered = qualityFrom2018.filter(q => new Date(q.Year, q.Month - 1, 1) >= qualityCutoff);
  const qualityByMonth = {};
  qualityMonthlyFiltered.forEach(q => {
    const key = `${q.Year}-${String(q.Month).padStart(2, '0')}`;
    if (!qualityByMonth[key]) { qualityByMonth[key] = { year: q.Year, month: q.Month }; QUALITY_SCORE_KEYS.forEach(k => qualityByMonth[key][k] = 0); }
    QUALITY_SCORE_KEYS.forEach(k => qualityByMonth[key][k] += q[k]);
  });
  const qualityMonthKeys = Object.keys(qualityByMonth).sort();
  const qualityMonthlyData = qualityMonthKeys.length > 0 ? {
    labels: qualityMonthKeys.map(k => `${MONTH_NAMES[qualityByMonth[k].month - 1]} ${qualityByMonth[k].year}`),
    datasets: QUALITY_SCORE_KEYS.map((key, i) => ({
      label: `${i + 1}`,
      data: qualityMonthKeys.map(k => { const m = qualityByMonth[k]; const total = QUALITY_SCORE_KEYS.reduce((sum, sk) => sum + m[sk], 0); return total > 0 ? m[key] / total * 100 : 0; }),
      backgroundColor: QUALITY_SCORE_COLORS[i].bg,
      borderColor: QUALITY_SCORE_COLORS[i].border,
      borderWidth: 1,
    })),
  } : null;

  // Yearly: aggregate by year
  const qualityByYear = {};
  qualityFrom2018.forEach(q => {
    if (!qualityByYear[q.Year]) { qualityByYear[q.Year] = {}; QUALITY_SCORE_KEYS.forEach(k => qualityByYear[q.Year][k] = 0); }
    QUALITY_SCORE_KEYS.forEach(k => qualityByYear[q.Year][k] += q[k]);
  });
  const qualityYears = Object.keys(qualityByYear).sort();
  const qualityYearlyData = qualityYears.length > 0 ? {
    labels: qualityYears,
    datasets: QUALITY_SCORE_KEYS.map((key, i) => ({
      label: `${i + 1}`,
      data: qualityYears.map(y => { const yr = qualityByYear[y]; const total = QUALITY_SCORE_KEYS.reduce((sum, sk) => sum + yr[sk], 0); return total > 0 ? yr[key] / total * 100 : 0; }),
      backgroundColor: QUALITY_SCORE_COLORS[i].bg,
      borderColor: QUALITY_SCORE_COLORS[i].border,
      borderWidth: 1,
    })),
  } : null;

  const qualityOptions = {
    maintainAspectRatio: false,
    legend: { position: 'top' },
    tooltips: { callbacks: { label: function(item, data) { return `Score ${data.datasets[item.datasetIndex].label}: ${item.yLabel.toFixed(1)}%`; } } },
    scales: {
      xAxes: [{ stacked: true }],
      yAxes: [{ stacked: true, ticks: { max: 100, callback: function(value) { return value + '%'; } }, scaleLabel: { display: true, labelString: 'Percentage' } }],
    },
  };

  const qualityMonthlyOptions = { ...qualityOptions, title: { display: true, text: 'Quality of Property by Month', fontSize: 16 } };
  const qualityYearlyOptions = { ...qualityOptions, title: { display: true, text: 'Quality of Property by Year', fontSize: 16 } };

  const lateVsOnTimeData = lateCategories.length > 0 ? {
    labels: lateCategories.map(c => c.Name),
    datasets: [
      { label: 'Late (> 30 days)', data: lateCategories.map(c => c.Late || 0), backgroundColor: '#d71433' },
      { label: 'On Time', data: lateCategories.map(c => (c.Count || 0) - (c.Late || 0)), backgroundColor: '#94ba33' },
    ],
  } : null;

  // --- Scheduling projection (appointment-setting throughput -> end of month) ---
  const projection = dashData?.result?.SchedulingProjection;

  // --- 90-day "scheduled per day" chart (30 days back -> today -> 60 days forward) ---
  const schedByDay = dashData?.result?.ScheduledByDay || [];
  const schedByDayMap = {};
  schedByDay.forEach(p => { schedByDayMap[p.date] = p.count; });
  const sched90Labels = [];
  const sched90Data = [];
  const sched90Colors = [];
  const _today90 = new Date();
  for (let i = -30; i <= 60; i++) {
    const d = new Date(_today90.getFullYear(), _today90.getMonth(), _today90.getDate() + i);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    sched90Labels.push(i === 0 ? 'Today' : `${d.getDate()} ${MONTH_NAMES[d.getMonth()]}`);
    sched90Data.push(schedByDayMap[key] || 0);
    if (i < 0) { sched90Colors.push('rgba(107, 114, 128, 0.55)'); } // past — gray
    else if (i === 0) { sched90Colors.push('#00acdc'); } // today — blue
    else { sched90Colors.push('rgba(0, 172, 220, 0.4)'); } // future — light blue
  }
  const sched90ChartData = {
    labels: sched90Labels,
    datasets: [{ label: 'Scheduled', data: sched90Data, backgroundColor: sched90Colors, borderColor: sched90Colors, borderWidth: 1 }],
  };
  // Future scheduled = open inspections with appointment_date >= today, same
  // definition as the Scheduled card (so the two headline figures match).
  const scheduledCount = categories.find(c => c.Name === 'Scheduled')?.Count || 0;
  const sched90ChartOptions = {
    maintainAspectRatio: false,
    legend: { display: false },
    tooltips: { mode: 'index', intersect: false, callbacks: { label: (item) => `${item.yLabel.toLocaleString()} inspections with an appointment` } },
    scales: {
      xAxes: [{ ticks: { autoSkip: true, maxTicksLimit: 15 } }],
      yAxes: [{ ticks: { beginAtZero: true, precision: 0 }, scaleLabel: { display: true, labelString: 'Inspections scheduled' } }],
    },
  };

  // --- Incoming inspections projection to end of month ---
  // Uses the incoming-monthly-trend series: current month cumulative so far vs
  // last month's cumulative at the same window-relative day (day 1 = the last
  // day of the prior month, matching the Received card's window), scaled to
  // last month's final total. Fetched via the API client (no SWR) on mount.
  const [incomingTrend, setIncomingTrend] = useState(null);
  useEffect(() => {
    apiReports.getIncomingMonthlyTrend()
      .then((resp) => setIncomingTrend(resp?.data?.result))
      .catch(() => {});
  }, []);
  const incomingProjection = useMemo(() => {
    const r = incomingTrend;
    if (!r) {return null;}
    const sum = (pts) => (pts || []).reduce((s, p) => s + (p.count || 0), 0);
    const soFar = sum(r.current?.points);
    // Project at the current month's own 7-day receive rate × days left, the
    // same method as the appts projection beside it. The previous method added
    // last month's tail after the same day-of-month, which drags the forecast
    // sideways when the two months end on different weekdays (e.g. August
    // ended on a dead weekend, September on weekdays).
    const last7 = (r.current?.points || []).filter((p) => p.day > r.todayDay - 7).reduce((s, p) => s + (p.count || 0), 0);
    const rate7d = last7 / 7;
    // Days left comes from the backend (app-timezone, safe on the last day of
    // the month where todayDay-derived math wrapped to 30).
    const daysToEom = Math.max(typeof r.daysToEom === 'number' ? r.daysToEom : 0, 0);
    const projected = soFar + Math.round(rate7d * daysToEom);
    return { soFar, projected };
  }, [incomingTrend]);

  // --- Late Management tab data (only fetch when tab active) ---
  const [dataLoading, setDataLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(`${DateTime.now().year}-${DateTime.now().month}`);

  const { data: liveData } = useSWR(activeTab === 2 ? `${config.api.baseUrl}/reports/inspectionlates/live` : null, { refreshInterval: 30000 });
  const { data: timeData } = useSWR(activeTab === 2 ? `${config.api.baseUrl}/reports/inspectionlates/period?month=${selectedMonth}` : null, { refreshInterval: 30000 });
  const { data: lastThreeMonths } = useSWR(activeTab === 0 || activeTab === 2 ? `${config.api.baseUrl}/reports/inspectionlates/last3months` : null, { refreshInterval: 0 });

  // --- Penalty Risk tab data (only fetch when tab active) ---
  const { data: penaltyRiskData, mutate: mutatePenaltyRisk } = useSWR(activeTab === 3 ? `${config.api.baseUrl}/reports/penaltyrisk` : null, { refreshInterval: 30000 });
  const { data: penaltyRiskDigest, mutate: mutateDigest } = useSWR(
    activeTab === 3 ? `${config.api.baseUrl}/reports/penaltyrisk/digest` : null,
    { refreshInterval: penaltyRiskDigest?.result?.status === 'generating' ? 3000 : 0 }
  );

  useEffect(() => { if (timeData) {setDataLoading(false);} }, [timeData]);

  const monthSelections = useMemo(() => {
    let res = [];
    const currentMonth = DateTime.now().startOf('month');
    for (let i = 0; i < 13; i++) {
      const thisMonth = currentMonth.minus({ months: i });
      res.push({ id: `${thisMonth.year}-${thisMonth.month}`, display: `${thisMonth.monthShort} ${thisMonth.year}` });
    }
    return res;
  }, []);

  function changeMonth(value) { setDataLoading(true); setSelectedMonth(value); }

  // --- Penalty Risk working-list claim/release ---
  const currentUserId = config.user.id;
  // A claim is live if claimedByUserId is set; claims persist until released.
  const claimIsLive = (item) => !!item.claimedByUserId;
  const myClaimedItems = () =>
    (penaltyRiskData?.result?.items || []).filter(i => claimIsLive(i) && i.claimedByUserId === currentUserId);

  const claimSelected = async (noAction = false) => {
    const ids = Array.from(riskSelected);
    if (!ids.length) { return; }
    const results = await Promise.all(ids.map(id => apiReports.claimPenaltyRisk(id, noAction).catch(e => e)));
    const claimedIds = new Set();
    let failed = 0;
    results.forEach((r, idx) => {
      const id = ids[idx];
      if (r instanceof Error) { failed++; return; }
      if (r?.data?.status === 200 && r?.data?.result?.success) {
        claimedIds.add(id);
      } else if (r?.data?.status === 200 && r?.data?.result?.success === false) {
        const who = r.data.result.claimedByInitials || 'someone';
        toast.error(`Already claimed by ${who}`);
        failed++;
      } else {
        failed++;
      }
    });
    if (claimedIds.size > 0) {
      toast.success(`Claimed ${claimedIds.size} inspection${claimedIds.size > 1 ? 's' : ''}${noAction ? ' (no action required)' : ''}`);
      applyClaimOptimistic(claimedIds, noAction);
    }
    if (failed > 0 && claimedIds.size === 0) { toast.error('Failed to claim some inspections'); }
    setRiskSelected(new Set());
  };

  const releaseClaim = async (inspectionId) => {
    const res = await apiReports.releasePenaltyRisk(inspectionId).catch(e => e);
    if (res instanceof Error || res?.data?.status !== 200 || res?.data?.result?.success === false) {
      toast.error('Failed to release claim');
      return;
    }
    toast.success('Released');
    applyReleaseOptimistic(inspectionId);
  };

  // Toggle one of my claims between "action required" and "no action required".
  const setClaimState = async (inspectionId, noAction) => {
    const res = await apiReports.setPenaltyRiskClaimState(inspectionId, noAction).catch(e => e);
    if (res instanceof Error || res?.data?.status !== 200 || res?.data?.result?.success === false) {
      toast.error('Failed to update claim');
      return;
    }
    toast.success(noAction ? 'Marked no action required' : 'Marked action required');
    applyClaimStateOptimistic(inspectionId, noAction);
  };

  const toggleRiskSelect = (id) => {
    setRiskSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  // Initials for the current user, derived client-side from config.user.name so
  // optimistic updates show correct initials without waiting on a refetch.
  const myInitials = (() => {
    const parts = (config.user.name || '').split(' ').map(s => s.trim()).filter(Boolean);
    if (!parts.length) { return '—'; }
    if (parts.length === 1) { return parts[0].charAt(0).toUpperCase(); }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  })();

  // Optimistically apply claim state to the SWR cache so the mini-list and the
  // Claimed column update instantly (the server GET response is HTTP-cached and
  // can lag). claimedIds is a Set of inspectionIds claimed by me this round.
  const applyClaimOptimistic = (claimedIds, noAction = false) => {
    const cur = penaltyRiskData;
    if (!cur?.result?.items) { return; }
    const now = new Date().toISOString();
    const items = cur.result.items.map(it => claimedIds.has(it.id)
      ? { ...it, claimedByUserId: currentUserId, claimedByInitials: myInitials, claimedAt: now, claimedNoAction: noAction }
      : it);
    mutatePenaltyRisk({ ...cur, result: { ...cur.result, items } }, false);
  };

  const applyClaimStateOptimistic = (inspectionId, noAction) => {
    const cur = penaltyRiskData;
    if (!cur?.result?.items) { return; }
    const items = cur.result.items.map(it => it.id === inspectionId
      ? { ...it, claimedNoAction: noAction }
      : it);
    mutatePenaltyRisk({ ...cur, result: { ...cur.result, items } }, false);
  };

  const applyReleaseOptimistic = (inspectionId) => {
    const cur = penaltyRiskData;
    if (!cur?.result?.items) { return; }
    const items = cur.result.items.map(it => it.id === inspectionId
      ? { ...it, claimedByUserId: null, claimedByInitials: '', claimedAt: null, claimedNoAction: false }
      : it);
    mutatePenaltyRisk({ ...cur, result: { ...cur.result, items } }, false);
  };

  // Shared row cells for the risk table (Risk -> Claimed). Used by both the
  // main table and the My Working List so the two stay column-aligned.
  const renderRiskCells = (item) => {
    const riskColors = {
      'critical': 'tw-bg-fs-red tw-text-white',
      'high': 'tw-bg-fs-red-light tw-text-white',
      'watch': 'tw-bg-yellow-400',
      'stalled': 'tw-bg-fs-dark-blue-light tw-text-white',
      'closeout-pending': 'tw-bg-fs-red-dark tw-text-white',
      'appt-past': 'tw-bg-purple-500 tw-text-white',
    };
    const riskLabels = {
      'critical': '🔴 Critical',
      'high': '🟠 High',
      'watch': '🟡 Watch',
      'stalled': '⚠️ Stalled',
      'closeout-pending': '📋 Closeout',
      'appt-past': '📅 Appt Passed',
    };
    return <>
      <td className="tw-px-6 tw-py-2">
        <span className={`tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-semibold tw-whitespace-nowrap ${riskColors[item.riskLevel] || ''}`}>
          {riskLabels[item.riskLevel] || item.riskLevel}
        </span>
      </td>
      <td className="tw-px-6 tw-py-2">
        <a href={`/inspections/${item.policyNo}/process/pre_r`} target="_blank" rel="noopener noreferrer" className="tw-text-primary hover:tw-underline">
          {item.policyNo}
        </a>
      </td>
      <td className="tw-px-6 tw-py-2">{item.inspectionType || '—'}</td>
      <td className="tw-px-6 tw-py-2">{item.prettyStatus || item.status}</td>
      <td className="tw-px-6 tw-py-2">{item.policyStatus}</td>
      <td className="tw-px-6 tw-py-2">{item.city ? `${item.city}, ${item.state}` : item.state || '—'}</td>
      <td className="tw-px-6 tw-py-2">{item.ageDays}d</td>
      <td className="tw-px-6 tw-py-2">
        <span className={item.daysRemaining <= 3 ? 'tw-text-fs-red tw-font-bold' : item.daysRemaining <= 8 ? 'tw-text-fs-red-light tw-font-semibold' : ''}>
          {item.daysRemaining}
        </span>
      </td>
      <td className="tw-px-6 tw-py-2">
        <a href="#" onClick={(e) => { e.preventDefault(); setContactPolicyNo(item.policyNo); }} className="tw-text-primary hover:tw-underline"
          title={item.lastMessage ? `Last message: “${item.lastMessage}”${item.lastMessageAt ? ' — ' + new Date(item.lastMessageAt).toLocaleString() : ''}` : undefined}>
          {item.contactAttempts}
        </a>
      </td>
      <td className="tw-px-6 tw-py-2">
        {item.lastContactDate ? new Date(item.lastContactDate).toLocaleDateString() : '—'}
      </td>
      <td className="tw-px-6 tw-py-2">
        {(() => {
          if (!item.appointmentDate) {return '—';}
          const d = new Date(item.appointmentDate);
          const appt = new Date(d); appt.setHours(0,0,0,0);
          const today = new Date(); today.setHours(0,0,0,0);
          // A past appointment isn't a problem if an extension was approved with
          // an extended deadline on or after the appointment.
          let extDeadline = item.extensionApprovedDate && item.extendedDeadline ? new Date(item.extendedDeadline) : null;
          if (extDeadline) { extDeadline.setHours(0,0,0,0); }
          const coveredByExt = extDeadline && appt <= extDeadline;
          const showRed = appt < today && !coveredByExt;
          return showRed
            ? <span className="tw-text-fs-red tw-font-bold">{d.toLocaleDateString()}</span>
            : d.toLocaleDateString();
        })()}
      </td>
      <td className="tw-px-6 tw-py-2">
        {(() => {
          const today = new Date(); today.setHours(0,0,0,0);
          if (item.extensionApprovedDate) {
            // extensionApprovedDate is the date the approval was recorded; the
            // date the extension is FOR is extendedDeadline.
            const ed = item.extendedDeadline ? new Date(item.extendedDeadline) : new Date(item.extensionApprovedDate);
            ed.setHours(0,0,0,0);
            const daysToDeadline = Math.ceil((ed - today) / 86400000);
            // Green while there's room, orange as the deadline approaches (≤7 days), red once passed.
            const cls = daysToDeadline <= 0 ? 'tw-text-fs-red' : daysToDeadline <= 7 ? 'tw-text-orange-400' : 'tw-text-green-600';
            const state = daysToDeadline <= 0 ? ' (deadline reached)' : daysToDeadline <= 7 ? ' (closing)' : '';
            return (
              <span className={`tw-font-bold ${cls}`} title={`Approved through ${ed.toLocaleDateString()}${state}`}>
                <span>✓</span><span className="tw--ml-1">✓</span>
              </span>
            );
          }
          if (item.extensionRequestedDate) {
            // Awaiting Orchid: extensionRequestedDate is the day the ask went
            // out. Gray while fresh, orange after 3 days of silence, red after 7.
            let cls = 'tw-text-gray-400';
            let note = '';
            const asked = new Date(item.extensionRequestedDate); asked.setHours(0,0,0,0);
            const daysWaiting = Math.floor((today - asked) / 86400000);
            if (daysWaiting >= 7) { cls = 'tw-text-fs-red'; note = ' (no response 7+ days)'; }
            else if (daysWaiting >= 3) { cls = 'tw-text-orange-400'; note = ' (no response 3+ days)'; }
            const thru = item.extendedDeadline ? new Date(item.extendedDeadline).toLocaleDateString() : null;
            return <span className={`${cls} tw-font-semibold`} title={`Requested ${asked.toLocaleDateString()}${thru ? `, through ${thru}` : ''}${note}`}>✓</span>;
          }
          return '—';
        })()}
      </td>
      <td className="tw-px-6 tw-py-2">
        {(() => {
          // "Visited": enough recorded photos (>5) means the inspector actually
          // went out and did the work, even if the inspection isn't completed.
          if (!item.photoCount || item.photoCount <= 5) { return '—'; }
          return (
            <a href="#" onClick={(e) => { e.preventDefault(); setPhotoPopupPolicyNo(item.policyNo); }}
              className="tw-text-green-600 tw-font-bold"
              title={`${item.photoCount} photos recorded — click to view`}>
              ✓
            </a>
          );
        })()}
      </td>
    </>;
  };

  const liveDataCalc = useMemo(() => {
    if (!liveData) {return null;}
    const late = Number(liveData.result.Late) || 0;
    const due = Number(liveData.result.ComingDue) || 0;
    const ok = liveData.result.Open - due - late;
    const total = late + due + ok;
    return { ok, due, late, okPercent: ok / total * 100, duePercent: due / total * 100, latePercent: late / total * 100 };
  }, [liveData]);

  const policyStatus = dashData?.result?.PolicyStatus || { New: 0, Renewal: 0 };
  const policyStatusTotal = policyStatus.New + policyStatus.Renewal;
  const policyStatusBar = 
    <div className="tw-w-full tw-h-full tw-flex tw-flex-col tw-justify-center">
      <div className="tw-flex tw-h-6 tw-rounded-lg tw-overflow-hidden">
        <div className="tw-bg-fs-green" style={{ width: `${policyStatusTotal > 0 ? policyStatus.New / policyStatusTotal * 100 : 0}%` }} title={`New: ${policyStatus.New}`} />
        <div className="tw-bg-primary" style={{ width: `${policyStatusTotal > 0 ? policyStatus.Renewal / policyStatusTotal * 100 : 0}%` }} title={`Renewal: ${policyStatus.Renewal}`} />
      </div>
      <div className="tw-flex tw-justify-between tw-text-base tw-mt-3">
        <span>New {policyStatus.New} ({policyStatusTotal > 0 ? (policyStatus.New / policyStatusTotal * 100).toFixed(0) : 0}%)</span>
        <span>Renewal {policyStatus.Renewal} ({policyStatusTotal > 0 ? (policyStatus.Renewal / policyStatusTotal * 100).toFixed(0) : 0}%)</span>
      </div>
    </div>
  ;

  const liveDataCategoryCalc = useMemo(() => {
    if (!liveData) {return null;}
    const late30 = liveData.result.LateCategories.Late1 || 0;
    const late45 = liveData.result.LateCategories.Late2 || 0;
    const late180 = liveData.result.LateCategories.Late3 || 0;
    const excluded = liveData.result.Excluded || 0;
    const total = late30 + late45 + late180 + excluded;
    return { late30, late45, late180, excluded, late30Percent: late30 / total * 100, late45Percent: late45 / total * 100, late180Percent: late180 / total * 100, excludedPercent: excluded / total * 100 };
  }, [liveDataCalc]);

  const livePieData = useMemo(() => {
    if (!liveDataCalc) {return null;}
    return { datasets: [{ data: [liveDataCalc.ok, liveDataCalc.due, liveDataCalc.late], backgroundColor: [green, amber, red] }], labels: ["OK", "Coming Due", "Late"] };
  }, [liveDataCalc]);

  const lateCategoryPieData = useMemo(() => {
    if (!liveDataCategoryCalc) {return null;}
    return { datasets: [{ data: [liveDataCategoryCalc.late30, liveDataCategoryCalc.late45, liveDataCategoryCalc.late180, liveDataCategoryCalc.excluded], backgroundColor: [yellow, amber, red, black] }], labels: ["Late > 30 days", "Late > 45 days", "Late > 180 days", "Excluded Lates"] };
  }, [liveDataCategoryCalc]);

  const timePieData = useMemo(() => {
    if (!timeData) {return null;}
    const onTime = (Number(timeData.result.Summary.Submitted) || 0) - (Number(timeData.result.Summary.Late) || 0);
    const late = Number(timeData.result.Summary.Late) || 0;
    return { datasets: [{ data: [onTime, late], backgroundColor: [green, red] }], labels: ["On time", "Late"] };
  }, [timeData]);

  const get3MonthSummaryData = ({ Created, Submitted }) =>
    <div className={"tw-grid tw-grid-cols-3 tw-gap-2"}>
      <p className={"tw-text-sm"}>Received</p><p className={"tw-text-sm"}>Submitted</p><p className={"tw-text-sm"}>Turnover</p>
      <p className={"tw-text-xl"}>{Created}</p><p className={"tw-text-xl"}>{Submitted}</p><p className={"tw-text-xl"}>{Created > 0 ? (Submitted / Created * 100).toFixed(0) : 0}%</p>
    </div>
  ;

  // =============================================
  // RENDER
  // =============================================
  return (
    <>
      <ToastContainer autoClose={3000} />
      <div className="tw-flex tw-items-center tw-gap-4 tw-mb-4">
        <h1>360 Management Overview</h1>
        {loading && <CircularProgress size={24} />}
      </div>

      <div className="tw-bg-white tw-mb-6">
        <StyledTabs
          value={activeTab}
          onChange={(e, v) => {
            setActiveTab(v);
            const anchor = TABS[v] && TABS[v].anchor;
            if (anchor) { window.history.replaceState(null, "", "#" + anchor); }
          }}
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {TABS.map((t) => <StyledTab key={t.anchor} label={t.label} />)}
        </StyledTabs>
      </div>

      {/* ==================== SCHEDULING TAB ==================== */}
      <TabPanel value={activeTab} index={0} componentName="mgmt">
        <div className="tw-mb-2 tw-text-lg">
          <span className="tw-font-semibold">{total}</span> open inspections
          {hasYesterday && <span className="tw-ml-3 tw-text-sm"><ChangeIndicator change={totalChange} sentiment="neutral" /></span>}
        </div>

        {/* Current-month submission turnover (mirrors the Late Management card) */}
        <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-5 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-6">
          {categoryNames.map((name, i) => {
            const cat = categories.find(c => c.Name === name);
            const typeMap = { 'Scheduled': 'scheduled', 'Part Scheduled': 'partsched', 'Not Scheduled': 'notsched', 'New': 'new' };
            const metricMap = { 'Scheduled': 'sched_scheduled', 'Part Scheduled': 'sched_part_scheduled', 'Not Scheduled': 'sched_not_scheduled', 'New': 'sched_new' };
            const cardInfo = {
              'Scheduled': 'Open inspections with an appointment dated today or later. Past appointments that did not complete are counted in the Late buckets, not here.',
              'Part Scheduled': 'Open inspections with no appointment yet, but with work history — someone has contacted the policyholder or saved notes. Being worked, no date set.',
              'Not Scheduled': 'Open inspections with no appointment and no work history, older than 14 days — sitting untouched.',
              'New': 'Open inspections with no appointment and no work history that arrived in the last 14 days — fresh intake the schedulers have not worked yet.',
            }[name];
            return <DashboardCard key={name} title={name} value={cat?.Count} color={cardColors[i]} info={cardInfo} onClick={() => window.open(`/reports/late/list/${typeMap[name]}`, "_blank")} sparklineData={historyByMetric[metricMap[name]]} secondValue={name === 'Scheduled' ? scheduledMonthLine : null} />;
          })}
          {lastThreeMonths?.result?.[4] &&
            <DashboardCard title={`Current Month — ${lastThreeMonths.result[4].MonthName}`} value={get3MonthSummaryData(lastThreeMonths.result[4])} color="tw-bg-green-600" info={`Inspections received vs submitted for this month. Received counts from the last day of the prior month; Turnover = Submitted ÷ Received.`} />
          }
        </div>

        {/* Scheduling projection to end of month (appointment-setting throughput) */}
        {projection &&
          <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-6 tw-my-8">
            <h4 className="tw-text-lg tw-font-semibold tw-mb-1">Scheduling projection &mdash; end of month</h4>
            <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
              Appointments set this month, projected to month end at the current 7-day rate. Incoming inspections projected at the current 7-day receive rate.
            </p>
            <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-4 md:tw-gap-8">
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
                <p className="tw-text-xs tw-text-gray-500">Set this month <InfoIcon text="Appointments made since the 1st, counted from inspection history. An inspection's latest appointment event must be 'Appointment made', so re-booked policies count once and cancellations net out." /></p>
                <p className="tw-text-2xl tw-font-bold">{projection.setThisMonthSoFar}</p>
              </div>
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
                <p className="tw-text-xs tw-text-gray-500">7-day rate <InfoIcon text="Appointments made in the last 7 days ÷ 7 — the current booking pace." /></p>
                <p className="tw-text-2xl tw-font-bold tw-text-primary">{Math.round(projection.rate7d)}/day</p>
              </div>
              {/* 30-day rate — commented out, may restore later
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
                <p className="tw-text-xs tw-text-gray-500">30-day rate</p>
                <p className="tw-text-2xl tw-font-bold tw-text-primary">{Math.round(projection.rate30d)}/day</p>
              </div>
              */}
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
                <p className="tw-text-xs tw-text-gray-500">Future scheduled <InfoIcon text="Open inspections with an appointment today or later — the same figure as the Scheduled card above." /></p>
                <p className="tw-text-2xl tw-font-bold">{scheduledCount}</p>
              </div>
              <div className="tw-bg-fs-dark-blue tw-bg-opacity-10 tw-rounded-lg tw-p-3">
                <p className="tw-text-xs tw-text-gray-500">Appts projected by EOM (7-day rate) <InfoIcon text="Set this month + 7-day rate × days left in the month." /></p>
                <p className="tw-text-2xl tw-font-bold tw-text-primary">{projection.projectedEom7d}</p>
              </div>
              {/* Projected by EOM (30-day rate) — commented out, may restore later
              <div className="tw-bg-fs-dark-blue tw-bg-opacity-10 tw-rounded-lg tw-p-3">
                <p className="tw-text-xs tw-text-gray-500">Projected by EOM (30-day rate)</p>
                <p className="tw-text-2xl tw-font-bold tw-text-primary">{projection.projectedEom30d}</p>
              </div>
              */}
              <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
                <p className="tw-text-xs tw-text-gray-500">Incoming so far <InfoIcon text="Inspections received in this month's window. The window starts on the last day of the prior month, matching the Current Month — Received card." /></p>
                <p className="tw-text-2xl tw-font-bold">{incomingProjection ? incomingProjection.soFar : '---'}</p>
              </div>
              <div className="tw-bg-fs-dark-blue tw-bg-opacity-10 tw-rounded-lg tw-p-3">
                <p className="tw-text-xs tw-text-gray-500">Incoming projected by EOM <InfoIcon text="Received so far + the current 7-day receive rate × days left in the month." /></p>
                <p className="tw-text-2xl tw-font-bold tw-text-primary">{incomingProjection ? incomingProjection.projected : '---'}</p>
              </div>
            </div>
          </div>
        }

        {/* 90-day scheduled-per-day chart: 30 days back -> today -> 60 days forward */}
        <div className="tw-my-8">
          <h4 className="tw-text-xl tw-font-bold">Scheduled per day (90 days)</h4>
          <p className="tw-text-sm tw-text-gray-600 tw-mb-4">
            Inspections with an appointment on each day &mdash; past 30 days (gray), today (blue), next 60 days (light blue).
          </p>
          <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4" style={{ height: '360px' }}>
            <Bar data={sched90ChartData} options={sched90ChartOptions} />
          </div>
        </div>

        {lateCategories.length > 0 &&
          <>
            <h4 className="tw-text-lg tw-font-semibold tw-mt-8 tw-mb-4">Late Inspections ({'>'} 30 days)</h4>
            <div className="tw-grid tw-grid-cols-3 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-4">
              {lateNames.map((name, i) => {
                const cat = lateCategories.find(c => c.Name === name);
                const late = cat?.Late || 0;
                const count = cat?.Count || 0;
                const pct = count > 0 ? Math.round(late / count * 100) : 0;
                const lateTypeMap = { 'Scheduled': 'scheduled-late', 'Part Scheduled': 'partsched-late', 'Not Scheduled': 'notsched-late' };
                const metricMap = { 'Scheduled': 'sched_scheduled_late', 'Part Scheduled': 'sched_part_scheduled_late', 'Not Scheduled': 'sched_not_scheduled_late' };
                return <DashboardCard key={name} title={name} value={late} subtitle={`of ${count}`} secondValue={count > 0 ? `${pct}% are late` : null} color={cardColors[i]} onClick={() => window.open(`/reports/late/list/${lateTypeMap[name]}`, "_blank")} sparklineData={historyByMetric[metricMap[name]]} />;
              })}
            </div>
          </>
        }

        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-8 tw-my-8">
          {doughnutData &&
            <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-6">
              <h4 className="tw-text-lg tw-font-semibold tw-mb-4">Open Inspection Breakdown</h4>
              <div className="tw-grid tw-grid-cols-2 tw-gap-4">
                <div className="tw-flex tw-flex-col tw-justify-center tw-gap-3">
                  {categories.map((cat, i) => {
                    const pct = total > 0 ? (cat.Count / total * 100).toFixed(0) : 0;
                    return (
                      <div key={cat.Name} className="tw-flex tw-items-center tw-gap-2">
                        <span className="tw-w-8 tw-h-3 tw-inline-block tw-rounded-sm" style={{ backgroundColor: chartColors[i] }} />
                        <p className="tw-text-sm">{cat.Name} - {cat.Count} ({pct}%)</p>
                      </div>
                    );
                  })}
                </div>
                <div><Doughnut data={doughnutData} options={doughnutOptions} /></div>
              </div>
            </div>
          }
          {lateVsOnTimeData &&
            <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-6">
              <h4 className="tw-text-lg tw-font-semibold tw-mb-4">Late vs On Time</h4>
              <div style={{ height: '220px' }}><HorizontalBar data={lateVsOnTimeData} options={lateBarOptions} /></div>
            </div>
          }
          <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-6">
            <h4 className="tw-text-lg tw-font-semibold tw-mb-4">New vs Renewal</h4>
            {policyStatusBar}
          </div>
        </div>

        <IncomingMonthlyTrend />

        <IncomingInspectionsMap />

        {(monthlyTrendData || yearlyTrendData) &&
          <>
            <h4 className="tw-text-xl tw-font-bold tw-mt-10 tw-mb-2">Turnaround Time</h4>
            <hr className="tw-border-primary tw-mb-6" />
            <h5 className="tw-text-lg tw-font-semibold tw-mb-4">Monthly Trends</h5>
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-mb-10">
              {monthlyTrendData && <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-primary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Bar data={monthlyTrendData} options={trendOptions} /></div></div>}
              {monthlyDistData && <div className="tw-border tw-border-secondary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-secondary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Bar data={monthlyDistData} options={distOptions} /></div></div>}
            </div>
            <h5 className="tw-text-lg tw-font-semibold tw-mb-4">Yearly Trends {years.length > 0 && <span className="tw-font-normal tw-text-gray-500">· {years[0]} – {years[years.length - 1]}</span>}</h5>
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-mb-10">
              {yearlyTrendData && <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-primary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Bar data={yearlyTrendData} options={trendOptions} /></div></div>}
              {yearlyDistData && <div className="tw-border tw-border-secondary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-secondary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Bar data={yearlyDistData} options={distOptions} /></div></div>}
            </div>
          </>
        }

        {(sqftTrendData || sqftDistData) &&
          <>
            <h4 className="tw-text-xl tw-font-bold tw-mt-10 tw-mb-2">Square Footage</h4>
            <hr className="tw-border-primary tw-mb-6" />
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-mb-10">
              {sqftDistData && <div className="tw-border tw-border-secondary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-secondary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><HorizontalBar data={sqftDistData} options={sqftDistOptions} /></div></div>}
              {sqftTrendData && <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-primary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Line data={sqftTrendData} options={sqftTrendOptions} /></div></div>}
            </div>
          </>
        }

        {(qualityMonthlyData || qualityYearlyData) &&
          <>
            <h4 className="tw-text-xl tw-font-bold tw-mt-10 tw-mb-2">Quality of Property</h4>
            <hr className="tw-border-primary tw-mb-6" />
            <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-8 tw-mb-10">
              {qualityMonthlyData && <div className="tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-primary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Bar data={qualityMonthlyData} options={qualityMonthlyOptions} /></div></div>}
              {qualityYearlyData && <div className="tw-border tw-border-secondary tw-rounded-lg tw-shadow-lg tw-overflow-hidden"><div className="tw-bg-secondary tw-w-full tw-p-2" /><div className="tw-p-4" style={{ height: '400px' }}><Bar data={qualityYearlyData} options={qualityYearlyOptions} /></div></div>}
            </div>
          </>
        }
      </TabPanel>

      {/* ==================== LATE MANAGEMENT TAB ==================== */}
      {/* ==================== COMPLETED TAB ==================== */}
      <TabPanel value={activeTab} index={1} componentName="mgmt">
        <CompletedTab />
      </TabPanel>

      {/* ==================== LATE MANAGEMENT TAB ==================== */}
      <TabPanel value={activeTab} index={2} componentName="mgmt">
        {!config.externalAdmin &&
          <>
            <h4>Submission Turnover</h4>
            <div className={"tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 xl:tw-grid-cols-5 tw-gap-8 tw-my-6"}>
              <DashboardCard title={lastThreeMonths && lastThreeMonths.result && lastThreeMonths.result[0].MonthName || "---"} value={lastThreeMonths && lastThreeMonths.result && get3MonthSummaryData(lastThreeMonths.result[0])}/>
              <DashboardCard title={lastThreeMonths && lastThreeMonths.result && lastThreeMonths.result[1].MonthName || "---"} value={lastThreeMonths && lastThreeMonths.result && get3MonthSummaryData(lastThreeMonths.result[1])}/>
              <DashboardCard title={lastThreeMonths && lastThreeMonths.result && lastThreeMonths.result[2].MonthName || "---"} value={lastThreeMonths && lastThreeMonths.result && get3MonthSummaryData(lastThreeMonths.result[2])}/>
              <DashboardCard title={"Total"} value={lastThreeMonths && lastThreeMonths.result && get3MonthSummaryData(lastThreeMonths.result[3])} color={"tw-bg-gray-400"}/>
              <DashboardCard title={lastThreeMonths && lastThreeMonths.result && lastThreeMonths.result[4].MonthName || "---"} value={lastThreeMonths && lastThreeMonths.result && get3MonthSummaryData(lastThreeMonths.result[4])} color={"tw-bg-green-600"}/>
            </div>
            {line}
          </>
        }

        <h4>Live</h4>
        <div className={"tw-mt-6 lg:tw-grid tw-grid-cols-2 tw-gap-8 tw-justify-start"}>
          <div className={"tw-mb-10 tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4"}>
            <div className={"tw-grid tw-grid-cols-2 tw-h-full"}>
              <div className={"tw-flex tw-flex-col tw-justify-center tw-gap-2"}>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-bg-fs-green tw-w-8 tw-h-3 tw-inline-block"}/><p>{`OK - ${liveDataCalc ? liveDataCalc.ok : ""} (${liveDataCalc ? liveDataCalc.okPercent.toFixed(0) : ""}%)`}</p></div>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-bg-yellow-400 tw-w-8 tw-h-3 tw-inline-block"}/><p>{`Coming Due - ${liveDataCalc ? liveDataCalc.due : ""} (${liveDataCalc ? liveDataCalc.duePercent.toFixed(0) : ""}%)`}</p></div>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-bg-fs-red tw-w-8 tw-h-3 tw-inline-block"}/><p>{`Late - ${liveDataCalc ? liveDataCalc.late : ""} (${liveDataCalc ? liveDataCalc.latePercent.toFixed(0) : ""}%)`}</p></div>
              </div>
              <div>{livePieData && <Pie data={livePieData} options={livePieOptions}/>}</div>
            </div>
          </div>

          <div className={"tw-mb-10 tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4"}>
            <div className={"tw-grid tw-grid-cols-2 tw-h-full"}>
              <div className={"tw-flex tw-flex-col tw-justify-center tw-gap-2"}>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-w-8 tw-h-3 tw-inline-block"} style={{ backgroundColor: '#F8ED00' }}/><p>{`Late > 30 days - ${liveDataCategoryCalc ? liveDataCategoryCalc.late30 : ""} (${liveDataCategoryCalc ? liveDataCategoryCalc.late30Percent.toFixed(0) : ""}%)`}</p></div>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-bg-yellow-400 tw-w-8 tw-h-3 tw-inline-block"}/><p>{`Late > 45 days - ${liveDataCategoryCalc ? liveDataCategoryCalc.late45 : ""} (${liveDataCategoryCalc ? liveDataCategoryCalc.late45Percent.toFixed(0) : ""}%)`}</p></div>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-bg-fs-red tw-w-8 tw-h-3 tw-inline-block"}/><p>{`Late > 180 days - ${liveDataCategoryCalc ? liveDataCategoryCalc.late180 : ""} (${liveDataCategoryCalc ? liveDataCategoryCalc.late180Percent.toFixed(0) : ""}%)`}</p></div>
                <div className={"tw-flex tw-items-center tw-gap-2"}><span className={"tw-bg-black tw-w-8 tw-h-3 tw-inline-block"}/><p>{`Excluded - ${liveDataCategoryCalc ? liveDataCategoryCalc.excluded : ""} (${liveDataCategoryCalc ? liveDataCategoryCalc.excludedPercent.toFixed(0) : ""}%)`}</p></div>
              </div>
              <div>{lateCategoryPieData && <Pie data={lateCategoryPieData} options={livePieOptions}/>}</div>
            </div>
          </div>
        </div>

        <div className={"tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-6"}>
          <DashboardCard title={"All Open"} value={liveData?.result?.Open} secondValue={liveData?.result?.Open > 0 ? `avg ${Math.round(liveData?.result?.OpenContacts / liveData?.result?.Open)} contacts` : null} onClick={() => window.open("/reports/late/list/allopen", "_blank")} sparklineData={historyByMetric['live_all_open']} />
          <DashboardCard title={"Late > 30 days"} value={liveData && liveData.result && GetLate(liveData.result)} secondValue={liveData?.result?.Late > 0 ? `avg ${Math.round(liveData?.result?.LateContacts / liveData?.result?.Late)} contacts` : null} onClick={() => window.open("/reports/late/list/late", "_blank")} sparklineData={historyByMetric['live_late_30']} />
          <DashboardCard title={"Coming Due (in 5 days)"} value={liveData && liveData.result && GetComingDue(liveData.result)} secondValue={liveData?.result?.ComingDue > 0 ? `avg ${Math.round(liveData?.result?.ComingDueContacts / liveData?.result?.ComingDue)} contacts` : null} onClick={() => window.open("/reports/late/list/comingdue", "_blank")} sparklineData={historyByMetric['live_coming_due_5']} />
          <DashboardCard title={"Submitted last 30 days"} value={liveData && liveData.result && liveData.result.SubmittedLastThirtyDays} secondValue={liveData && liveData.result && `Last 5 days: ${liveData.result.SubmittedLastFiveDays}`} sparklineData={historyByMetric['live_submitted_30']} />
        </div>

        <div className={"tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-6"}>
          <DashboardCard title={"New (today)"} value={liveData?.result?.ReceivedToday} secondValue={liveData?.result?.ReceivedLastThirtyDays > 0 ? `Last 30 days: ${liveData.result.ReceivedLastThirtyDays}` : null} sparklineData={historyByMetric['live_received_today']} />
        </div>

        {!config.externalAdmin &&
          <>
            {line}
            <h4 className="tw-mt-8">Monthly</h4>
            <div className="tw-mt-4 tw-flex tw-gap-4 tw-text-lg tw-items-center tw-h-14">
              <p>Reporting period</p>
              <select className="tw-rounded-lg" value={selectedMonth} onChange={(e) => changeMonth(e.target.value)}>
                {monthSelections.map(monthSelection => <option key={monthSelection.id} value={monthSelection.id}>{monthSelection.display}</option>)}
              </select>
              {dataLoading && <CircularProgress color="primary"/>}
            </div>
            <div className="tw-mt-6 tw-w-1/2 tw-mx-auto tw-mb-10 tw-border tw-border-primary tw-rounded-lg tw-shadow-lg tw-p-4">
              {timePieData && <Pie data={timePieData} options={MonthlyPieOptions} height={60}/>}
            </div>
            <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-6">
              <DashboardCard title="Received" value={timeData?.result?.Summary?.Received}/>
              <DashboardCard title="Submitted" value={timeData?.result?.Summary?.Submitted}/>
              <DashboardCard title="Late" value={timeData?.result?.Summary && `${timeData.result.Summary.Late} (${timeData.result.Summary.LatePercent.toFixed(0)}%)`}/>
              <DashboardCard title="Turnover" value={timeData?.result?.Summary && `${timeData.result.Summary.Turnover.toFixed(0)}%`}/>
            </div>
          </>
        }

        {lateDashData?.result &&
          <>
            <hr className="tw-border-primary tw-my-8 tw-mb-16" />
            <h4 className="tw-text-xl tw-font-bold tw-mb-4">Late Performance</h4>
            <div className="lg:tw-grid tw-grid-cols-2 tw-gap-8 tw-mb-16">
              <div>
                <h5 className="tw-text-lg tw-font-semibold tw-mb-3">By State</h5>
                <LateDashStateTable data={lateDashData.result.States} />
              </div>
              <div>
                <h5 className="tw-text-lg tw-font-semibold tw-mb-3">By Inspector</h5>
                <LateDashInspectorTable data={lateDashData.result.Inspectors} />
              </div>
            </div>
          </>
        }
      </TabPanel>

      {/* ==================== PENALTY RISK TAB ==================== */}
      {/* ==================== PENALTY RISK TAB ==================== */}
      <TabPanel value={activeTab} index={3} componentName="mgmt">
        {/* AI Daily Digest */}
        {penaltyRiskDigest?.result?.status === 'ready' && 
          <div className="tw-bg-fs-dark-blue tw-bg-opacity-10 tw-rounded-lg tw-p-4 tw-mb-6 tw-border tw-border-fs-dark-blue tw-border-opacity-20">
            <div className="tw-flex tw-items-start tw-gap-3">
              <span className="tw-text-lg">⚠️</span>
              <div>
                <p className="tw-text-sm tw-font-semibold tw-text-fs-dark-blue tw-mb-1">Daily Risk Summary <button className="tw-text-xs tw-text-primary hover:tw-underline tw-ml-2" onClick={async () => { await fetch(`${config.api.baseUrl}/reports/penaltyrisk/digest?refresh=true`, { headers: { Authorization: `Bearer ${config.api.jwt}` } }); mutateDigest(); }}>Regenerate</button></p>
                <p className="tw-text-sm tw-text-gray-700">{penaltyRiskDigest.result.summary}</p>
                <p className="tw-text-xs tw-text-gray-400 tw-mt-2">
                  Generated {new Date(penaltyRiskDigest.result.generatedAt).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        }
        {penaltyRiskDigest?.result?.status === 'generating' && 
          <div className="tw-bg-fs-dark-blue tw-bg-opacity-10 tw-rounded-lg tw-p-4 tw-mb-6">
            <div className="tw-flex tw-items-center tw-gap-3">
              <CircularProgress size={20} />
              <span className="tw-text-sm tw-text-gray-600">Generating daily risk summary...</span>
            </div>
          </div>
        }

        {/* Summary cards row; subtitle shows how many of my claimed items fall in each category */}
        {(() => {
          const mineSub = (lvl) => {
            const mine = myClaimedItems().filter(i => i.riskLevel === lvl);
            if (!mine.length) { return null; }
            const a = mine.filter(i => !i.claimedNoAction).length;
            const n = mine.length - a;
            return `Mine: ${a} action, ${n} no action`;
          };
          const total = penaltyRiskData?.result?.summary?.total || 0;
          return (
            <div data-tour="pr-cards" className="tw-mt-4">
              <p className="tw-text-sm tw-text-gray-600">
                <strong>{total}</strong> open inspections at risk
                {riskFilter ? ` — showing ${riskFilter.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} below` : ''}
                {showButton && <button onClick={startTour} className="tw-ml-3 tw-px-3 tw-py-1 tw-border tw-rounded tw-text-sm hover:tw-bg-gray-50">Take the tour</button>}
              </p>
              <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-6 tw-gap-4 md:tw-gap-8 xl:tw-gap-16 tw-my-6">
                <DashboardCard title="🔴 Critical (≤3 days)" value={penaltyRiskData?.result?.summary?.critical || 0} subtitle={mineSub('critical')} color="tw-bg-fs-red" selected={riskFilter === 'critical'} onClick={() => setRiskFilter(riskFilter === 'critical' ? null : 'critical')} />
                <DashboardCard title="🟠 High (≤8 days, no appt)" value={penaltyRiskData?.result?.summary?.high || 0} subtitle={mineSub('high')} color="tw-bg-fs-red-light" selected={riskFilter === 'high'} onClick={() => setRiskFilter(riskFilter === 'high' ? null : 'high')} />
                <DashboardCard title="🟡 Watch (15+ days, no appt)" value={penaltyRiskData?.result?.summary?.watch || 0} subtitle={mineSub('watch')} color="tw-bg-yellow-400" selected={riskFilter === 'watch'} onClick={() => setRiskFilter(riskFilter === 'watch' ? null : 'watch')} />
                <DashboardCard title="⚠️ Stalled (no contact 6+ days)" value={penaltyRiskData?.result?.summary?.stalled || 0} subtitle={mineSub('stalled')} color="tw-bg-fs-dark-blue-light" selected={riskFilter === 'stalled'} onClick={() => setRiskFilter(riskFilter === 'stalled' ? null : 'stalled')} />
                <DashboardCard title="📋 Closeout Pending (6+ attempts)" value={penaltyRiskData?.result?.summary?.closeoutPending || 0} subtitle={mineSub('closeout-pending')} color="tw-bg-fs-red-dark" selected={riskFilter === 'closeout-pending'} onClick={() => setRiskFilter(riskFilter === 'closeout-pending' ? null : 'closeout-pending')} />
                <DashboardCard title="📅 Appt Passed, not completed" value={penaltyRiskData?.result?.summary?.apptPast || 0} subtitle={mineSub('appt-past')} color="tw-bg-purple-500" selected={riskFilter === 'appt-past'} onClick={() => setRiskFilter(riskFilter === 'appt-past' ? null : 'appt-past')} />
              </div>
            </div>
          );
        })()}

        {/* My lists — inspections I've claimed from this tab, split by action state */}
        {(() => {
          const allMine = myClaimedItems();
          const mine = riskFilter && listsMatchFilter
            ? allMine.filter(i => i.riskLevel === riskFilter)
            : allMine;
          const myListSection = (title, items, actions) => 
            <div className="tw-rounded-lg tw-border tw-border-fs-dark-blue tw-border-opacity-30 tw-overflow-hidden">
              <div className="tw-bg-fs-dark-blue tw-bg-opacity-10 tw-px-4 tw-py-2">
                <span className="tw-text-sm tw-font-semibold tw-text-fs-dark-blue">{title} ({items.length})</span>
              </div>
              <div className="tw-overflow-x-auto">
                <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
                  <thead className="tw-bg-gray-100">
                    <tr>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Risk</th>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Policy #</th>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Type</th>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Status</th>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Policy Type</th>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Location</th>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Age</th>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Days Left</th>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Contacts</th>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Last Contact</th>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Appt</th>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Ext.</th>
                      <th className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider">Vis.</th>
                      <th className="tw-px-6 tw-py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="tw-divide-y tw-divide-gray-100">
                    {items.map((item, i) =>
                      <tr key={item.id} className={i % 2 ? 'tw-bg-gray-50' : ''}>
                        {renderRiskCells(item)}
                        <td className="tw-px-6 tw-py-1 tw-text-right">
                          <DropdownButton title="Actions" size="xsmall" endIcon={<ArrowDropDown style={{ fontSize: "1rem" }} />} options={actions(item)} onSelectOption={(opt) => onListAction(item, opt)} />
                        </td>
                      </tr>
                    )}
                    {!items.length &&
                      <tr><td colSpan={15} className="tw-px-6 tw-py-4 tw-text-sm tw-text-gray-500">Nothing here yet — claim inspections from the table below.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          ;
          const onListAction = (item, opt) => {
            if (opt === 'Release') { releaseClaim(item.id); }
            else { setClaimState(item.id, opt === 'No action needed'); }
          };
          const actionRequired = mine.filter(i => !i.claimedNoAction);
          const noAction = mine.filter(i => i.claimedNoAction);
          const filterLabel = riskFilter ? riskFilter.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : null;
          return (
            <div data-tour="pr-mylist" className="tw-mb-6 tw-rounded-lg tw-border tw-border-gray-300 tw-overflow-hidden">
              <div className="tw-bg-gray-100 tw-px-4 tw-py-2 tw-flex tw-items-center tw-justify-between">
                <span className="tw-text-sm tw-font-semibold tw-text-gray-700">My List ({allMine.length})</span>
                {riskFilter &&
                  <label className="tw-inline-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-600">
                    <input type="checkbox" checked={listsMatchFilter} onChange={() => setListsMatchFilter(v => !v)} />
                    Match current filter ({filterLabel})
                  </label>}
              </div>
              <div className="tw-p-3 tw-space-y-3">
                {myListSection('Action required', actionRequired, () => ['No action needed', 'Release'])}
                {myListSection('No action required', noAction, () => ['Needs work', 'Release'])}
              </div>
            </div>
          );
        })()}

        {/* Claim-selected toolbar */}
        {riskSelected.size > 0 &&
          <div className="tw-mb-3 tw-flex tw-items-center tw-gap-3">
            <span className="tw-text-sm tw-text-gray-700">{riskSelected.size} selected</span>
            <button className="tw-px-3 tw-py-1 tw-text-sm tw-bg-fs-dark-blue tw-text-white tw-rounded hover:tw-opacity-90" onClick={() => claimSelected(false)}>Claim selected</button>
            <button className="tw-px-3 tw-py-1 tw-text-sm tw-bg-gray-500 tw-text-white tw-rounded hover:tw-opacity-90" onClick={() => claimSelected(true)}>Claim selected (no action)</button>
            <button className="tw-text-sm tw-text-gray-500 hover:tw-underline" onClick={() => setRiskSelected(new Set())}>Clear</button>
          </div>
        }

        {/* Risk table */}
        {/* Risk table toolbar: total, active filter, search */}
        {(() => {
          const all = penaltyRiskData?.result?.items || [];
          if (!all.length) { return null; }
          const q = riskSearch.trim().toLowerCase();
          const shown = all.filter(item =>
            (!riskFilter || item.riskLevel === riskFilter) &&
            (!q || [item.policyNo, item.address, item.city, item.state].some(v => (v || '').toLowerCase().includes(q))));
          return (
            <div data-tour="pr-toolbar" className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
              <span className="tw-text-sm tw-text-gray-600">Showing <strong>{shown.length}</strong> of <strong>{all.length}</strong></span>
              {riskFilter &&
                <>
                  <span className="tw-text-sm tw-text-gray-600">Filtered: <strong>{riskFilter.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</strong></span>
                  <button className="tw-text-sm tw-text-primary hover:tw-underline" onClick={() => setRiskFilter(null)}>Clear filter</button>
                </>}
              <input
                type="search"
                value={riskSearch}
                onChange={(e) => setRiskSearch(e.target.value)}
                placeholder="Search policy #, address, city, state…"
                className="tw-ml-auto tw-px-3 tw-py-1.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary"
                style={{ minWidth: '240px' }}
              />
            </div>
          );
        })()}
        {(() => {
          const allItems = penaltyRiskData?.result?.items || [];
          const q = riskSearch.trim().toLowerCase();
          const filteredItems = allItems.filter(item =>
            (!riskFilter || item.riskLevel === riskFilter) &&
            (!q || [item.policyNo, item.address, item.city, item.state].some(v => (v || '').toLowerCase().includes(q))));
          const toggleSort = (k) => {
            if (riskSortKey === k) {setRiskSortDir(riskSortDir === 'asc' ? 'desc' : 'asc');}
            else { setRiskSortKey(k); setRiskSortDir('asc'); }
          };
          const sortValue = (item, k) => {
            if (k === 'location') {return `${item.city || ''}, ${item.state || ''}`;}
            if (k === 'lastContactDate' || k === 'appointmentDate') {return item[k] ? new Date(item[k]).getTime() : 0;}
            // Ext.: earliest extended deadline first; no extension sorts last.
            if (k === 'extendedDeadline') {return item.extendedDeadline ? new Date(item.extendedDeadline).getTime() : Number.MAX_SAFE_INTEGER;}
            return item[k];
          };
          const sortedItems = [...filteredItems].sort((a, b) => {
            if (!riskSortKey) {return 0;}
            const av = sortValue(a, riskSortKey), bv = sortValue(b, riskSortKey);
            const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
            return riskSortDir === 'asc' ? cmp : -cmp;
          });
          const th = (label, k) => 
            <th key={k} className="tw-px-6 tw-py-3 tw-text-left tw-font-medium tw-uppercase tw-tracking-wider tw-cursor-pointer tw-select-none hover:tw-text-primary" onClick={() => toggleSort(k)}>
              {label} {riskSortKey === k ? riskSortDir === 'asc' ? '▲' : '▼' : ''}
            </th>
          ;
          return sortedItems.length > 0 ? 
            <div data-tour="pr-table" className="tw-overflow-x-auto">
              <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
                <thead>
                  <tr className="tw-bg-fs-dark-blue tw-bg-opacity-25">
                    <th className="tw-px-4 tw-py-3"></th>
                    {th('Risk', 'riskLevel')}
                    {th('Policy #', 'policyNo')}
                    {th('Type', 'inspectionType')}
                    {th('Status', 'prettyStatus')}
                    {th('Policy Type', 'policyStatus')}
                    {th('Location', 'location')}
                    {th('Age', 'ageDays')}
                    {th('Days Left', 'daysRemaining')}
                    {th('Contacts', 'contactAttempts')}
                    {th('Last Contact', 'lastContactDate')}
                    {th('Appt', 'appointmentDate')}
                    {th('Ext.', 'extendedDeadline')}
                    {th('Vis.', 'photoCount')}
                    {th('Claimed', 'claimedByInitials')}
                  </tr>
                </thead>
                <tbody>
                  {sortedItems.map((item, i) => {
                    return (
                      <tr key={item.policyNo} className={i % 2 ? 'tw-bg-gray-200' : ''}>
                        <td className="tw-px-4 tw-py-2">
                          <input
                            type="checkbox"
                            disabled={claimIsLive(item)}
                            checked={riskSelected.has(item.id)}
                            onChange={() => toggleRiskSelect(item.id)}
                          />
                        </td>
                        {renderRiskCells(item)}
                        <td className="tw-px-6 tw-py-2">
                          {claimIsLive(item) ?
                            <>
                              <span className={`tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-semibold ${item.claimedByUserId === currentUserId ? 'tw-bg-fs-dark-blue tw-text-white' : 'tw-bg-gray-300 tw-text-gray-700'}`} title={item.claimedAt ? new Date(item.claimedAt).toLocaleString() : ''}>
                                {item.claimedByInitials || '—'}
                              </span>
                              {item.claimedNoAction && <span className="tw-ml-1" title="No action required">💤</span>}
                            </>
                            : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            : 
            <div className="tw-text-center tw-py-12 tw-text-gray-500">
              {penaltyRiskData ? `✅ No${riskFilter ? ' ' + riskFilter.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : ''} inspections at risk` : 'Loading...'}
            </div>
          ;
        })()}
      </TabPanel>

      {/* ==================== INSPECTOR UTILISATION TAB ==================== */}
      <TabPanel value={activeTab} index={tabIdx("utilisation")} componentName="mgmt">
        <InspectorUtilisationTab />
      </TabPanel>

      {/* ==================== SCHEDULER UTILISATION TAB ==================== */}
      <TabPanel value={activeTab} index={tabIdx("scheduler-utilisation")} componentName="mgmt">
        <SchedulerUtilisationTab />
      </TabPanel>

      {/* ==================== HEALTH MAP TAB ==================== */}
      <TabPanel value={activeTab} index={tabIdx("health-map")} componentName="mgmt">
        <HealthMapTab />
      </TabPanel>

      {/* ==================== OUT OF AREA TAB ==================== */}
      <TabPanel value={activeTab} index={tabIdx("out-of-area")} componentName="mgmt">
        <OutOfAreaTab />
      </TabPanel>

      {/* ==================== NATURAL LANGUAGE REPORTS TAB ==================== */}
      <TabPanel value={activeTab} index={tabIdx("ask-reports")} componentName="mgmt">
        <NaturalLanguageReportTab />
      </TabPanel>

      {/* ==================== PENALTY MANAGEMENT TAB (superusers only) ==================== */}
      {isSuperAdmin && 
        <TabPanel value={activeTab} index={tabIdx("penalty-management")} componentName="mgmt">
          <PipelineTab />
        </TabPanel>
      }

      {/* Contact dialog for penalty risk tab */}
      <Dialog open={!!contactPolicyNo} onClose={() => setContactPolicyNo(null)} maxWidth='xl' fullWidth={true}>
        <DialogContent>
          {contactPolicyNo && <InspectionPreContact policyNo={contactPolicyNo} />}
        </DialogContent>
      </Dialog>

      {/* Photos recorded on an inspection (Visited column in the risk table) */}
      <PhotoPopupDialog policyNo={photoPopupPolicyNo} onClose={() => setPhotoPopupPolicyNo(null)} />
    </>
  );
};

// PhotoPopupDialog shows the photos recorded on an inspection (first 8), so staff
// can see at a glance what the inspector actually captured.
const PhotoPopupDialog = ({ policyNo, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!policyNo) { setData(null); return; }
    setLoading(true);
    apiInspections.getPhotos(policyNo)
      .then((res) => setData(res.data?.status === 200 ? res.data.result : null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [policyNo]);
  return (
    <Dialog open={!!policyNo} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent>
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <h3 className="tw-text-base tw-font-semibold">Recorded photos — {policyNo}</h3>
          <button className="tw-text-sm tw-text-gray-500 hover:tw-underline" onClick={onClose}>Close</button>
        </div>
        {loading && <CircularProgress size={24} />}
        {!loading && data && data.photos.length === 0 &&
          <p className="tw-text-sm tw-text-gray-500">No photos recorded on this inspection.</p>}
        {!loading && data && data.photos.length > 0 &&
          <>
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-2">
              {data.photos.map((url, i) => <img key={i} src={url} alt={`photo ${i + 1}`} className="tw-w-full tw-rounded" />)}
            </div>
            {data.count > data.photos.length &&
              <p className="tw-text-xs tw-text-gray-500 tw-mt-2">Showing first {data.photos.length} of {data.count} photos.</p>}
          </>}
      </DialogContent>
    </Dialog>
  );
};

export default SupportDashboard;
